/**
 * Pure helper functions for score-table create/edit forms.
 * No react-admin or MUI imports — safe to import in tests without mocking.
 */

// ---------------------------------------------------------------------------
// computeCoverageWarnings
// ---------------------------------------------------------------------------

export interface CoverageRow {
  sex?: string;
  age_min?: number;
  age_max?: number;
  result_min: number;
  result_max: number;
  points: number;
}

export const DEMOGRAPHIC_SCORE_FIELDS = ['sex', 'age_min', 'age_max'] as const;

/** Detects prohibited demographic scoring keys by presence, including blank values. */
export function hasDemographicScoreFields(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(hasDemographicScoreFields);
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return DEMOGRAPHIC_SCORE_FIELDS.some(field => Object.prototype.hasOwnProperty.call(record, field))
    || Object.values(record).some(hasDemographicScoreFields);
}

function prohibitDemographicScoring(value: unknown): void {
  if (hasDemographicScoreFields(value)) {
    throw new Error('Age- or sex-specific scoring is prohibited. Remove sex, age_min, and age_max.');
  }
}

export function parseScoreTableCsv(text: string): { parsed: CoverageRow[]; errors: string[] } {
  const lines = text.split(/\r?\n/).filter(line => line.trim());
  if (lines.length < 2) return { parsed: [], errors: ['CSV must have a header row and at least one data row.'] };
  const headers = lines[0]!.toLowerCase().split(',').map(header => header.trim());
  if (DEMOGRAPHIC_SCORE_FIELDS.some(field => headers.includes(field))) {
    return { parsed: [], errors: ['Age- or sex-specific scoring is prohibited. Remove sex, age_min, and age_max.'] };
  }
  const index = (name: string) => headers.indexOf(name);
  if (['result_min', 'result_max', 'points'].some(name => index(name) < 0)) {
    return { parsed: [], errors: ['CSV must contain result_min, result_max, and points columns.'] };
  }
  const parsed: CoverageRow[] = [];
  const errors: string[] = [];
  for (let lineIndex = 1; lineIndex < lines.length; lineIndex++) {
    const parts = lines[lineIndex]!.split(',').map(part => part.trim());
    const raw = [parts[index('result_min')], parts[index('result_max')], parts[index('points')]];
    const [result_min, result_max, points] = raw.map(Number);
    if (raw.some(value => value === undefined || value === '') || ![result_min, result_max, points].every(Number.isFinite)) {
      errors.push(`Row ${lineIndex}: skipped — result_min, result_max, points are required numbers.`);
      continue;
    }
    parsed.push({ result_min, result_max, points });
  }
  return { parsed, errors };
}

export function computeCoverageWarnings(rows: CoverageRow[]): string[] {
  if (!rows || rows.length === 0) return [];
  const warnings: string[] = [];

  const hasDemographicSpecific = rows.some(row => hasDemographicScoreFields(row));
  if (hasDemographicSpecific) {
    warnings.push(
      'Historical demographic-specific rows are ineligible for scoring and are displayed only as reconstruction evidence.',
    );
  }

  const groupKey = (r: CoverageRow): string => {
    const sexPart = r.sex ?? '';
    const agePart = `${r.age_min ?? ''}-${r.age_max ?? ''}`;
    return `${sexPart}|${agePart}`;
  };

  const groupLabel = (r: CoverageRow): string => {
    if (r.sex) {
      const sexName = r.sex === 'M' ? 'Male' : r.sex === 'F' ? 'Female' : 'Other';
      return `${sexName} rows`;
    }
    return 'all rows';
  };

  const groups = new Map<string, CoverageRow[]>();
  for (const row of rows) {
    const key = groupKey(row);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }

  for (const [, groupRows] of groups) {
    const sorted = [...groupRows].sort((a, b) => a.result_min - b.result_min);
    const label = groupLabel(sorted[0]!);

    for (let i = 0; i < sorted.length - 1; i++) {
      const cur = sorted[i]!;
      const next = sorted[i + 1]!;

      if (cur.result_max + 1 < next.result_min) {
        warnings.push(`Gap between ${cur.result_max} and ${next.result_min} in ${label}.`);
      } else if (cur.result_max >= next.result_min) {
        warnings.push(`Ranges overlap near ${next.result_min} in ${label}.`);
      }
    }
  }

  return warnings;
}

// ---------------------------------------------------------------------------
// Row normalization
// ---------------------------------------------------------------------------

export interface RawBandRow {
  id: string;
  result_min: string;
  result_max: string;
  points: string;
}

export interface NormalizedBandRow {
  result_min: number;
  result_max: number;
  points: number;
}

export function normalizeRow(row: RawBandRow): NormalizedBandRow {
  const out: NormalizedBandRow = {
    result_min: Number(row.result_min),
    result_max: Number(row.result_max),
    points: Number(row.points),
  };
  return out;
}

// ---------------------------------------------------------------------------
// Row validation
// ---------------------------------------------------------------------------

export function validateBandRows(rows: RawBandRow[]): string[] {
  const errors: string[] = [];
  if (!rows || rows.length === 0) {
    errors.push('At least one scoring row is required.');
    return errors;
  }
  rows.forEach((row, i) => {
    const label = `Row ${i + 1}`;
    if (row.result_min === '' || isNaN(Number(row.result_min))) {
      errors.push(`${label}: Result Min is required and must be a number.`);
    }
    if (row.result_max === '' || isNaN(Number(row.result_max))) {
      errors.push(`${label}: Result Max is required and must be a number.`);
    }
    if (row.points === '' || isNaN(Number(row.points))) {
      errors.push(`${label}: Points is required and must be a number.`);
    }
    if (
      !isNaN(Number(row.result_min)) &&
      !isNaN(Number(row.result_max)) &&
      Number(row.result_min) > Number(row.result_max)
    ) {
      errors.push(`${label}: Result Min must be ≤ Result Max.`);
    }
    if (!isNaN(Number(row.points)) && Number(row.points) < 0) {
      errors.push(`${label}: Points must be ≥ 0.`);
    }
  });
  return errors;
}

// ---------------------------------------------------------------------------
// Version history sort
// ---------------------------------------------------------------------------

export interface VersionHistoryRecord {
  id: string;
  effective_date?: string;
  version?: string;
  status?: string;
  rows?: unknown[];
  measurement_id?: string;
}

export function sortVersionHistory(records: VersionHistoryRecord[]): VersionHistoryRecord[] {
  return [...records].sort((a, b) => {
    const dateA = a.effective_date ?? '';
    const dateB = b.effective_date ?? '';
    if (dateB !== dateA) return dateB.localeCompare(dateA);
    const verA = a.version ?? '';
    const verB = b.version ?? '';
    return verB.localeCompare(verA);
  });
}

// ---------------------------------------------------------------------------
// DataProvider payload types
// ---------------------------------------------------------------------------

export interface CsvSubmitPayload {
  event_id: string;
  measurement_id: string;
  label: string;
  effective_date: string;
  csv: string;
}

export interface GenerateSubmitPayload {
  event_id: string;
  measurement_id: string;
  label: string;
  effective_date: string;
  max_points: number;
  min_points: number;
  top_result: number;
  bottom_result: number;
}

export interface ManualSubmitPayload {
  event_id: string;
  measurement_id: string;
  label: string;
  effective_date: string;
  rows: NormalizedBandRow[];
  source: 'manual';
}

// Minimal DataProvider interface needed for submit helpers
export interface MinimalDataProvider {
  create: (
    resource: string,
    params: { data: Record<string, unknown> },
  ) => Promise<{ data: { id: string } }>;
}

export async function submitCsvImport(
  dataProvider: MinimalDataProvider,
  payload: CsvSubmitPayload,
): Promise<{ data: { id: string } }> {
  const validation = parseScoreTableCsv(payload.csv);
  if (validation.errors.length > 0 || validation.parsed.length === 0) {
    throw new Error(validation.errors.join(' ') || 'CSV must contain at least one valid scoring row.');
  }
  return dataProvider.create('score_tables/import-csv', {
    data: payload as unknown as Record<string, unknown>,
  });
}

export async function submitGenerate(
  dataProvider: MinimalDataProvider,
  payload: GenerateSubmitPayload,
): Promise<{ data: { id: string } }> {
  prohibitDemographicScoring(payload);
  return dataProvider.create('score_tables/generate', {
    data: payload as unknown as Record<string, unknown>,
  });
}

export async function submitManual(
  dataProvider: MinimalDataProvider,
  payload: ManualSubmitPayload,
): Promise<{ data: { id: string } }> {
  prohibitDemographicScoring(payload.rows);
  return dataProvider.create('score_tables', {
    data: payload as unknown as Record<string, unknown>,
  });
}

export interface D10CurvePoint {
  result: number;
  points: number;
}

export function d10CurveData(
  topResult: number,
  bottomResult: number,
  minPts: number,
  maxPts: number,
): D10CurvePoint[] {
  const range = topResult - bottomResult;
  return [
    { result: bottomResult, points: minPts },
    {
      result: bottomResult + range * 0.25,
      points: Math.round(minPts + 0.375 * (maxPts - minPts)),
    },
    {
      result: bottomResult + range * 0.75,
      points: Math.round(minPts + 0.625 * (maxPts - minPts)),
    },
    { result: topResult, points: maxPts },
  ];
}

export type ChipColor = 'success' | 'default' | 'warning' | 'error';

export interface StatusChipProps {
  color: ChipColor;
  label: string;
}

export function statusChipProps(status: string | undefined): StatusChipProps {
  if (!status || status === 'active') return { color: 'success', label: 'Active' };
  if (status === 'superseded') return { color: 'default', label: 'Superseded' };
  if (status === 'pending_approval') return { color: 'warning', label: 'Pending Approval' };
  return { color: 'error', label: 'Rejected' };
}
